<template>
  <div class="image-upload-dragger">
    <div class="upload-area">
      <a-upload-dragger
        :file-list="[]"
        :show-upload-list="false"
        name="file"
        :multiple="false"
        :custom-request="upload"
        accept=".png,.jpg,.jpeg,.gif"
      >
        <div v-if="!currentUrl" class="upload-empty">
          <LoadingOutlined v-if="uploading" />
          <PlusOutlined v-else />
          <span>{{ label }}</span>
          <small>{{ uploading ? '正在上传' : '点击或拖拽本地图片上传' }}</small>
        </div>
      </a-upload-dragger>

      <div
        v-if="currentUrl"
        class="upload-preview"
        :style="{ backgroundImage: `url(${currentUrl})` }"
      >
        <div class="preview-actions">
          <EyeOutlined title="查看图片" @click="previewVisible = true" />
          <DeleteOutlined title="删除图片" @click="removeImage" />
        </div>
      </div>
    </div>

    <a-modal
      :visible="previewVisible"
      :footer="null"
      title="图片预览"
      @cancel="previewVisible = false"
    >
      <img class="preview-image" :src="currentUrl" alt="图片预览" />
    </a-modal>
  </div>
</template>

<script>
import { computed, ref } from 'vue'
import {
  DeleteOutlined,
  EyeOutlined,
  LoadingOutlined,
  PlusOutlined
} from '@ant-design/icons-vue'
import { message } from 'ant-design-vue'
import { appTab_material_image_upload_post } from '@/server/modules/25824.js'

export default {
  name: 'ImageUploadDragger',
  components: {
    DeleteOutlined,
    EyeOutlined,
    LoadingOutlined,
    PlusOutlined
  },
  props: {
    fileList: {
      type: Array,
      default: () => []
    },
    label: {
      type: String,
      default: '点击上传'
    },
    materialType: {
      type: [Number, String],
      required: true
    }
  },
  emits: ['update:fileList'],
  setup(props, { emit }) {
    const uploading = ref(false)
    const previewVisible = ref(false)
    const currentFile = computed(() => props.fileList[0] || null)
    const currentUrl = computed(
      () => currentFile.value?.url || currentFile.value?.response?.url || ''
    )

    const removeImage = () => {
      emit('update:fileList', [])
      previewVisible.value = false
    }

    const upload = async ({ file, onError, onSuccess }) => {
      uploading.value = true
      try {
        const {
          success,
          data,
          retInfo
        } = await appTab_material_image_upload_post(file, props.materialType)
        if (!success || !data?.url) {
          const error = new Error(retInfo || '图片上传失败')
          onError(error)
          message.error(error.message)
          return
        }

        emit('update:fileList', [
          {
            uid: file.uid || `${Date.now()}`,
            name: file.name,
            status: 'done',
            url: data.url,
            width: Number(data.width) || undefined,
            height: Number(data.height) || undefined,
            uploaded: true,
            response: data
          }
        ])
        onSuccess(data, file)
      } catch (error) {
        onError(error)
        message.error(error?.message || '图片上传失败')
      } finally {
        uploading.value = false
      }
    }

    return {
      currentUrl,
      previewVisible,
      removeImage,
      upload,
      uploading
    }
  }
}
</script>

<style lang="scss" scoped>
.upload-area {
  position: relative;
  min-height: 100px;
}

:deep(.ant-upload.ant-upload-drag) {
  min-height: 100px;
  background: #fafafa;

  .ant-upload-btn {
    padding: 14px;
  }
}

.upload-empty {
  color: #595959;

  .anticon {
    display: block;
    margin-bottom: 6px;
    font-size: 22px;
  }

  span,
  small {
    display: block;
  }

  small {
    margin-top: 3px;
    color: #8c8c8c;
  }
}

.upload-preview {
  position: absolute;
  inset: 0;
  border: 1px solid #d9d9d9;
  border-radius: 2px;
  background: #fafafa center / contain no-repeat;
}

.preview-actions {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  gap: 18px;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  font-size: 20px;
  opacity: 0;
  transition: opacity 0.2s;

  .anticon {
    cursor: pointer;
  }
}

.upload-preview:hover .preview-actions {
  opacity: 1;
}

.preview-image {
  display: block;
  max-width: 100%;
  max-height: 70vh;
  margin: 0 auto;
}
</style>
